import React, { useEffect } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { TimerMode } from '../TimerEffect';

interface GradientEffectProps {
  progress: number;
  size: number;
  color: string;
  secondaryColor: string;
  opacity: number;
  speed: number;
  mode: TimerMode;
}

const GradientEffect: React.FC<GradientEffectProps> = ({
  progress,
  size,
  color,
  secondaryColor,
  opacity,
  speed,
  mode
}) => {
  // 动画控制
  const controls = useAnimationControls();
  
  // 根据模式调整渐变行为
  const isCountdown = mode === 'countdown';
  
  // 根据进度计算不透明度和强度
  const dynamicOpacity = Math.max(0.2, Math.min(0.8, opacity));
  
  // 根据进度计算旋转角度
  const rotationAngle = isCountdown
    ? 360 * (1 - progress) // 倒计时：从0到360度
    : 360 * progress; // 秒表：从0到360度
  
  // 启动旋转动画
  useEffect(() => {
    controls.start({
      rotate: rotationAngle,
      transition: {
        duration: 0.5 / speed,
        ease: "easeOut"
      }
    });
  }, [controls, rotationAngle, speed]);
  
  // 计算渐变大小（基于进度）
  const gradientSize = isCountdown
    ? size * (0.6 + progress * 0.4) // 倒计时：随进度减小
    : size * (0.6 + (1 - progress) * 0.4); // 秒表：随进度增大
  
  // 根据进度动态改变颜色亮度
  const getBrightness = (baseColor: string, factor: number): string => {
    // 假设baseColor是十六进制颜色代码，比如#FF6B6B
    // 移除#前缀并分解为RGB
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // 调整亮度
    const newR = Math.min(255, r * factor);
    const newG = Math.min(255, g * factor);
    const newB = Math.min(255, b * factor);
    
    // 转换回十六进制
    return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`;
  };
  
  // 计算亮度因子 - 倒计时越接近结束越亮，秒表随时间增加越亮
  const brightnessFactor = isCountdown
    ? 1 + (1 - progress) * 0.5 // 倒计时：从1到1.5
    : 1 + progress * 0.5; // 秒表：从1到1.5
  
  const brightColor = getBrightness(color, brightnessFactor);
  
  // 获取与主色匹配的更暗的颜色
  const darkColor = getBrightness(color, 0.3);
  
  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        borderRadius: '50%',
        overflow: 'hidden',
      }}
    >
      {/* 背景层 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${darkColor}33 0%, ${darkColor}00 70%)`,
          opacity: dynamicOpacity * 0.5,
        }}
      />
      
      {/* 渐变层 */}
      <motion.div
        animate={controls}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: gradientSize,
          height: gradientSize,
          borderRadius: '50%',
          background: `conic-gradient(
            ${brightColor} 0deg, 
            ${color} 90deg, 
            ${secondaryColor} 180deg, 
            ${color} 270deg, 
            ${brightColor} 360deg
          )`,
          opacity: dynamicOpacity * (isCountdown ? progress : 1 - progress),
          transform: 'translate(-50%, -50%)',
        }}
      />
      
      {/* 中心光晕 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: size * 0.4,
          height: size * 0.4,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${brightColor}99 0%, ${color}00 100%)`,
          opacity: dynamicOpacity,
          boxShadow: `0 0 ${size/10}px ${color}66`,
        }}
      />
      
      {/* 进度指示器 - 边缘光点 */}
      {progress > 0 && progress < 1 && (
        <div
          style={{
            position: 'absolute',
            top: size / 2 - size * 0.03,
            left: size - size * 0.06,
            width: size * 0.06,
            height: size * 0.06,
            borderRadius: '50%',
            background: brightColor,
            opacity: dynamicOpacity * 0.8,
            boxShadow: `0 0 ${size/20}px ${brightColor}`,
            transform: `rotate(${(isCountdown ? progress : 1 - progress) * 360}deg) translateY(-${size * 0.45}px)`,
            transformOrigin: 'center center',
          }}
        />
      )}
    </div>
  );
};

export default GradientEffect; 